import type { MessageBus } from '@setplex/mqbw'
import type { TPlayer } from 'video.js'

import { Events } from '|>/shared/events'

const bridge = Symbol()

function bridged<T = unknown>(msg: T): T {
  msg[bridge] = true
  return msg
}

function isBridged(msg: unknown): boolean {
  return typeof msg === 'object' && msg !== null && bridge in msg
}

export class VideoJSEvent {
  public readonly type: string
  public readonly payload: any

  public readonly player: TPlayer
  public readonly playerId: string

  constructor(
    event: unknown,
    data: unknown,
    player: TPlayer,
    playerId: string = player.id_
  ) {
    this.player = player
    this.playerId = playerId
    // custom and some of videojs events
    if (typeof event === 'string') {
      this.type = event
      this.payload = data
    }

    // videojs dom-based events
    else if (
      event !== null &&
      typeof event === 'object' &&
      'type' in event &&
      'metadata' in event &&
      typeof event.type === 'string'
    ) {
      this.type = event.type
      this.payload = event.metadata
    }

    // unknown event, should not happen, but just in case
    else {
      this.type = 'unknown'
      this.payload = { event, data }
    }
  }
}

export class NewPlayerInstantiation {
  static readonly topic = Events.Player.Instantiation
  public readonly player: TPlayer
  public readonly playerId: string
  public readonly type

  constructor(
    player: TPlayer,
    playerId: string = player.id_,
    type = Events.Player.Instantiation
  ) {
    this.player = player
    this.playerId = playerId
    this.type = type
  }
}

/**
 * Setup re-emission of video.js events to the message bus
 */
export function setup(mq: MessageBus, player: TPlayer) {
  const originalTrigger = player.trigger

  // override the trigger method to re-emit events to the message bus
  player.trigger = function (event: unknown, data: unknown) {
    // call the original trigger to ensure normal player behavior
    const result = originalTrigger.apply(player, arguments as any)

    // re-emit the event to the message bus
    // this creates "out" event
    const message = new VideoJSEvent(event, data, player)
    mq.emit(message.type, bridged(message))

    return result
  }

  // subscribe to inbound events
  const off = mq.on<unknown>((event) => {
    if (isBridged(event)) return // skip outbound "bridged" events

    let type: string | undefined
    let payload: unknown
    let playerId: unknown

    if ('type' in event && typeof event.type === 'string') {
      type = event.type
      payload = 'payload' in event ? event.payload : undefined
    } else if ('payload' in event && typeof event.payload === 'string') {
      type = event.payload
    }

    if ('playerId' in event) {
      playerId = event.playerId
    }

    // emit event to the player
    // if event has playerId, ensure it matches the current player
    if (type && (!playerId || playerId === player.id_)) {
      originalTrigger.call(player, type, payload as any)
    }
  })

  // cleanup on player dispose
  player.on('dispose', off)

  // emit player instance upon instantiation
  // this creates "out" event
  mq.emit(bridged(new NewPlayerInstantiation(player)))
}
